import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { ANONYMOUS_NODE_NAME, AvatarImage } from "api";
import { getNamingNameDetails } from "state/naming/selectors";
import { ClientState } from "state/state";
import Jump, { JumpCallback } from "ui/navigation/Jump";
import NodeNameText from "ui/nodename/NodeNameText";
import NodeNamePopup from "ui/nodename/NodeNamePopup";
import { useNameDisplayMode } from "ui/nodename/hooks";
import { NameDisplayMode } from "ui/types";
import { RelNodeName } from "util/rel-node-name";
import "./NodeName.css";

interface Props {
    name?: string | null;
    fullName?: string | null;
    sourceUri?: string | null;
    avatar?: AvatarImage | null;
    avatarNodeName?: RelNodeName | string;
    className?: string;
    verified?: boolean;
    correct?: boolean;
    linked?: boolean;
    popup?: boolean;
    display?: NameDisplayMode;
    onJump?: JumpCallback;
}

export default function NodeName({
    name, fullName, sourceUri, avatar, avatarNodeName, className, verified = false, correct = false, linked = true,
    popup = true, display, onJump
}: Props) {
    const details = useSelector((state: ClientState) => getNamingNameDetails(state, name));
    const mode = useNameDisplayMode();
    const {t} = useTranslation();

    if (!name) {
        return null;
    }

    const klass = cx(
        "node-name", {
            "correct": verified && correct,
            "incorrect": verified && !correct,
            linked,
        },
        className
    );
    const anonymous = name === ANONYMOUS_NODE_NAME;
    linked = linked && !anonymous && (!details.loaded || details.nodeUri != null);
    return (
        <NodeNamePopup nodeName={name} fullName={fullName} sourceUri={sourceUri} avatar={avatar}
                       avatarNodeName={avatarNodeName} disabled={!popup}>
            {ref =>
                linked ?
                    <Jump className={klass} nodeName={name} href="/" onNear={onJump} onFar={onJump} ref={ref}>
                        <NodeNameText nodeName={name} fullName={fullName} sourceUri={sourceUri}
                                      mode={display ?? mode}/>
                    </Jump>
                :
                    <span className={klass} title={anonymous ? t("anonymous-user") : undefined} ref={ref}>
                        <NodeNameText nodeName={name} fullName={fullName} sourceUri={sourceUri}
                                      mode={display ?? mode}/>
                    </span>
            }
        </NodeNamePopup>

    );
}
